/* eslint-disable react/display-name */
import React from 'react';
import axios from 'helpers/axios';

import { Container } from 'ui/components/styled';
import { DataTable, DataTableCell, DatePicker, IconButton } from 'ui/components/shared';
import { useToasts, useMutableRef, useBlobDownload, useCancelToken } from 'hooks';
import { lastDayOfPreviousMonth, formatDate } from 'helpers/date';
import { DownloadButton, CollapseIcon, Text, Badge } from '../styled';
import { ReportsByCategory } from '.';

export const ReportsByCustomer = ({
  company,
  date: initialDate = lastDayOfPreviousMonth(),
  DrawerCloseButton,
}) => {
  const [data, setData] = React.useState([]);
  const [footer, setFooter] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [totalRows, setTotalRows] = React.useState(0);
  const [perPage, setPerPage] = React.useState(15);
  const [sort, setSort] = React.useState({ column: '', order: '' });
  const [categorySortByCustomer, setCategorySortByCustomer] = React.useState({});
  const [search, setSearch] = React.useState('');
  const [date, setDate] = React.useState(initialDate);
  const { errorToast } = useToasts();
  const [currentPage, setCurrentPage] = useMutableRef(1);
  const { newCancelToken, cancelPreviousRequest, isCancel } = useCancelToken();
  const columns = [
    {
      name: 'Customer name',
      selector: 'customer.name',
      minWidth: '250px',
      sortable: true,
      //compact: true,
      grow: 2,
      cell: (row) => {
        const { customer } = row;
        if (row.id?.startsWith('footer-')) {
          return <DataTableCell>{customer?.name}</DataTableCell>;
        }

        const downloadLink = React.useMemo(() => {
          const formattedDate = formatDate(date);
          const orderby = categorySortByCustomer[row?.id]?.column || '';
          const order = categorySortByCustomer[row?.id]?.order || '';
          return `/reports/export/byCategory/${company?.id}/${customer?.id}/?$orderby=${orderby}&$order=${order}&$date=${formattedDate}`;
        }, [company, row, categorySortByCustomer, date]);
        const download = useBlobDownload(downloadLink);

        return (
          <DataTableCell centered>
            <IconButton
              icon="pdf-file"
              alt="download"
              title="Download"
              variant="transparent"
              size="16px"
              padding="8px" // keep the right icon size when text overflows
              mr="xxs"
              onClick={download.onClick}
              disabled={download.disabled}
            />
            <CollapseIcon mr="xxs" expanded={row.expanded} />
            <Text>{customer?.name}</Text>
            {customer?.archived && <Badge ml="xs">Archived</Badge>}
          </DataTableCell>
        );
      },
    },
    {
      name: 'Total Monitors',
      selector: 'jobs.total',
      //minWidth: '100px',
      sortable: true,
      compact: true,
      //center: true,
      style: { paddingLeft: '5px' },
    },
    {
      name: 'Success',
      selector: 'jobs.success',
      minWidth: 'auto',
      sortable: true,
      compact: true,
      right: true,
    },
    {
      name: 'Failed',
      selector: 'jobs.failed',
      minWidth: 'auto',
      sortable: true,
      compact: true,
      right: true,
    },
    {
      name: 'Warning',
      selector: 'jobs.warning',
      minWidth: 'auto',
      sortable: true,
      compact: true,
      right: true,
    },
    {
      name: 'Late',
      selector: 'jobs.late',
      minWidth: 'auto',
      sortable: true,
      compact: true,
      right: true,
    },
    {
      name: 'Uninitialized',
      selector: 'jobs.unknown',
      minWidth: '120px',
      sortable: true,
      //compact: true,
      right: true,
    },
  ];

  const fetchTotal = async (data, search, date) => {
    try {
      const response = await axios.get(
        `/reports/byCustomer/${company?.id}/total/?$search=${search}&$date=${date}`
      );

      if (response.status === 200) {
        setFooter([
          {
            customer: { name: 'Page Total' },
            jobs: data.reduce(
              (acc, { jobs }) => ({
                total: acc.total + jobs.total,
                unknown: acc.unknown + jobs.unknown,
                success: acc.success + jobs.success,
                failed: acc.failed + jobs.failed,
                warning: acc.warning + jobs.warning,
                late: acc.late + jobs.late,
              }),
              {
                total: 0,
                unknown: 0,
                success: 0,
                failed: 0,
                warning: 0,
                late: 0,
              }
            ),
          },
          {
            customer: { name: 'Grand Total' },
            jobs: response.data,
          },
        ]);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Total.' });
    }
  };

  const fetchReports = async (page) => {
    setLoading(true);

    try {
      const skip = page > 1 ? (page - 1) * perPage : 0;
      const formattedDate = formatDate(date);
      const response = await axios.get(
        `/reports/byCustomer/${company?.id}/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}&$date=${formattedDate}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
        await fetchTotal(response.data.value, search, formattedDate);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Reports.' });
    }

    setLoading(false);
  };

  const handleRowsPerPageChange = async (newPerPage, page) => {
    setLoading(true);
    setCurrentPage(page);

    try {
      const skip = page > 1 ? (page - 1) * newPerPage : 0;
      const formattedDate = formatDate(date);
      const response = await axios.get(
        `/reports/byCustomer/${company?.id}/?$top=${newPerPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}&$date=${formattedDate}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setPerPage(newPerPage);
        await fetchTotal(response.data.value, search, formattedDate);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Rows Change.' });
    }

    setLoading(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchReports(page);
  };

  const handleSort = async (column, sortDirection) => {
    setLoading(true);

    const sortColumn = column?.selector || '';
    const sortOrder = sortColumn ? sortDirection : '';
    setSort({ column: sortColumn, order: sortOrder });

    try {
      const skip = 0;
      const formattedDate = formatDate(date);
      const response = await axios.get(
        `/reports/byCustomer/${company?.id}/?$top=${perPage}&$skip=${skip}&$orderby=${sortColumn}&$order=${sortOrder}&$search=${search}&$date=${formattedDate}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
        await fetchTotal(response.data.value, search, formattedDate);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Sort Reports.' });
    }

    setLoading(false);
  };

  const handleFilter = async (value) => {
    setLoading(true);
    setSearch(value);

    cancelPreviousRequest();

    try {
      const skip = 0;
      const formattedDate = formatDate(date);
      const response = await axios.get(
        `/reports/byCustomer/${company?.id}/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${value}&$date=${formattedDate}`,
        { cancelToken: newCancelToken() }
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
        await fetchTotal(response.data.value, value, formattedDate);
      }
    } catch (error) {
      if (isCancel(error)) return;
      errorToast({ error, default: 'Error: Filter Reports.' });
    }

    setLoading(false);
  };

  const handleDateFilter = async (value) => {
    setLoading(true);
    setDate(value);

    try {
      const page = currentPage.current;
      const skip = page > 1 ? (page - 1) * perPage : 0;
      const formattedDate = formatDate(value);
      const response = await axios.get(
        `/reports/byCustomer/${company?.id}/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}&$date=${formattedDate}`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotalRows(response.data.count);
        await fetchTotal(response.data.value, search, formattedDate);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Filter Reports.' });
    }

    setLoading(false);
  };

  const handleCategorySortChange = (customer, categorySort) => {
    if (!customer) return;

    setCategorySortByCustomer({
      ...categorySortByCustomer,
      [customer.id]: categorySort,
    });
  };

  const handleRowExpandToggled = (toggleState, row) => {
    setData(data.map((item) => (item.id === row.id ? { ...item, expanded: toggleState } : item)));
  };

  React.useEffect(() => {
    fetchReports(1);
  }, []);

  const renderSubHeaderActions = () => (
    <Container flexDirection="row" alignItems="center" width="auto">
      <DatePicker placeholder="Date" format="MM/dd/yyyy" value={date} onChange={handleDateFilter} />
    </Container>
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        progressPending={loading}
        paginationPerPage={perPage}
        paginationTotalRows={totalRows}
        paginationCurrentPage={currentPage.current}
        onRowsPerPageChange={handleRowsPerPageChange}
        onPageChange={handlePageChange}
        onPageReset={setCurrentPage}
        onSort={handleSort}
        filterPlaceholder="Customer Search"
        filterProps={{ width: 'auto', maxWidth: '40%', flexGrow: 1 }}
        onFilter={handleFilter}
        subHeaderTitle={
          <SubHeaderTitle company={company} data={data} sort={sort} search={search} date={date} />
        }
        subHeaderTitleProps={{ minWidth: 'auto' }}
        subHeaderCloseButton={DrawerCloseButton}
        subHeaderActions={renderSubHeaderActions()}
        rowHeight="42px"
        expandableRows
        expandOnRowClicked
        expandableRowsHideExpander
        expandableRowsComponent={
          <ExpandedRowComponent
            company={company}
            date={date}
            onSortChange={handleCategorySortChange}
          />
        }
        //expandableIconWidth="42px"
        onRowExpandToggled={handleRowExpandToggled}
        footer={footer}
        noPadding
      />
    </>
  );
};

const SubHeaderTitle = ({ company, data, sort, search, date }) => {
  const downloadLink = React.useMemo(() => {
    const formattedDate = formatDate(date);
    return `/reports/export/byCustomer/${company?.id}/?$orderby=${sort.column}&$order=${sort.order}&$search=${search}&$date=${formattedDate}`;
  }, [company, sort, search, date]);
  const download = useBlobDownload(downloadLink);

  return (
    <Container flexDirection="row" alignItems="center" justifyContent="space-between">
      <span>{company?.name}</span>
      <DownloadButton
        ml="s"
        onClick={download.onClick}
        disabled={!data?.length || download.disabled}
      >
        Download
      </DownloadButton>
    </Container>
  );
};

const ExpandedRowComponent = ({ data, ...rest }) => (
  <ReportsByCategory customer={data.customer} {...rest} />
);
